import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import DateTimeSelector from './DateTimeSelector';
import user from '../assets/images/default.png';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useApi from '../hooks/useApi';
import ProductService from '../services/product.service';
import DomainService from '../services/domain.service';
import BookingService from '../services/booking.service';
import useAuth from '../hooks/useAuth';
import Modal from "react-modal";
import { useNavigate, useLocation } from "react-router-dom";
import DropdownTreeSelect from 'react-dropdown-tree-select';
import 'react-dropdown-tree-select/dist/styles.css';
import TherapistService from '../services/therapist.service';
import { Button } from '@mui/material';
import { useNotifications } from '../context/NotificationProvider';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import guestPlaceholder from '../assets/images/Frame_08.svg';
import { useLoading } from '../context/LoadingProvider';
import PromotionService from '../services/promotion.service';
import promotion_assignement_service from '../services/promotion_assignement_service';
import AvailableSlotsService from '../services/available_slots.service';
import { useTranslatableData } from "../hooks/useTranslatable";
import { transformTranslatableFields } from "../api/axios";
import { bindEvent, getPusher, initPusher } from "../services/pusher.service";

function GuestCard({
    index,
    product,
    durations = [],
    collapsed,
    onToggle,
    onSelectDuration,
    t,
}) {
    return (
        <div className="guest-card">
            <div
                className="guest-card__header"
                onClick={() => onToggle(index)}
                style={{ cursor: 'pointer' }}
            >
                <div className="guest-card__meta">
                    <img
                        src={guestPlaceholder}
                        alt=""
                        className="guest-card__avatar"
                    />
                    <span className="guest-card__label">{t('guest')} {index + 1}</span>
                </div>

                <KeyboardArrowDownIcon
                    className={`guest-card__chevron ${collapsed ? '' : 'open'}`}
                />
            </div>

            {!collapsed && durations.length > 0 && (
                <div className="duration-tabs">
                    {durations.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            className={`duration-tab ${product?.id === p.id ? 'duration-tab--active' : ''
                                }`}
                            onClick={() => onSelectDuration(index, p)}
                        >
                            <span className="duration-tab__minutes">{p.duration}&nbsp;{t('min')}</span>
                            <span className="duration-tab__price">{p.price}&nbsp;{t('chf')}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/* --- DuoInfoModal ---- */
function DuoInfoModal({ open, onClose, t1, t2, onAskRemove, t }) {
    const therapists = [t1, t2].filter(th => th && th.name);

    return (
        <Modal
            isOpen={open}
            onRequestClose={onClose}
            style={{
                content: {
                    width: '90%',
                    maxWidth: '480px',
                    maxHeight: '480px',
                    margin: 'auto',
                    padding: '30px',
                    borderRadius: '12px',
                    textAlign: 'center',
                },
            }}
        >
            <h2 style={{ marginBottom: 24 }}>{t('duo_order_detail')}</h2>

            {therapists.map((th, i) => (
                <div
                    key={i}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: '#FFF',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                        marginBottom: i < therapists.length - 1 ? 12 : 0,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <img
                            src={user}
                            alt=""
                            style={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                objectFit: 'cover',
                                background: '#F0F0F0',
                            }}
                        />
                        <div style={{ textAlign: 'left' }}>
                            <div style={{ fontWeight: 500, fontSize: '16px', color: '#333' }}>
                                {th.name}
                            </div>
                            {th.products.map((p) => (
                                <div
                                    key={p.id}
                                    style={{ fontSize: '14px', color: '#555', marginTop: 4 }}
                                >
                                    {`${Number(p.duration)} ${t('min')} – ${p.price} ${t('chf')}`}
                                </div>
                            ))}
                        </div>
                    </div>

                    <DeleteOutlineIcon
                        sx={{ color: '#B00020', cursor: 'pointer', fontSize: 24 }}
                        onClick={() => onAskRemove(i)}
                    />
                </div>
            ))}

            <button
                className="btn btn-secondary"
                onClick={onClose}
            >
                {t('close')}
            </button>
        </Modal>
    );
}

const hasOnlineTransactions = (booking) => {
  return booking?.transactions && 
         Array.isArray(booking.transactions) && 
         booking.transactions.some(transaction => 
           (transaction.succeeded_at !== null && transaction.succeeded_at !== undefined) ||
           ['COMPLETED', 'CONFIRMED', 'FULFILL'].includes(transaction.status)
         );
};

function CommandeEdit({ booking: rawBooking, bookingToEdit: rawBookingToEdit, onBack, onUpdate }) {
    const [rawBookingState, setRawBookingState] = useState(rawBooking);
    const [rawBookingToEditState, setRawBookingToEditState] = useState(rawBookingToEdit);
    const booking = useTranslatableData(rawBookingState);
    const bookingToEdit = useTranslatableData(rawBookingToEditState);

    const { t, i18n } = useTranslation('commande_edit');
    // Loader counter logic
    const originallyDuo = useRef(booking.isDuo).current;
    const loc = useLocation();
    const api = useApi();
    const { auth } = useAuth();
    const notify = useNotifications();
    const loader = useLoading();
    const refreshInFlightRef = useRef(false);
    const notifyRef = useRef(notify);
    const onBackRef = useRef(onBack);
    const refreshBookingRef = useRef(null);

    useEffect(() => {
        setRawBookingState(rawBooking);
        setRawBookingToEditState(rawBookingToEdit);
    }, [rawBooking, rawBookingToEdit]);

    useEffect(() => {
        notifyRef.current = notify;
    }, [notify]);

    useEffect(() => {
        onBackRef.current = onBack;
    }, [onBack]);

    const getZurichDate = useCallback((value) => {
        const date = value ? new Date(value) : new Date();
        return new Date(date.toLocaleString("en-US", { timeZone: "Europe/Zurich" }));
    }, []);

    const getZurichTime = useCallback((value) => {
        const date = value ? new Date(value) : new Date();
        return date
            .toLocaleTimeString("en-US", {
                timeZone: "Europe/Zurich",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            })
            .slice(0, 5);
    }, []);

    const bookingDateInZurich = getZurichDate(booking.start_before);
    const [selectedDate, setSelectedDate] = useState(bookingDateInZurich);

    const [availableHours, setAvailableHours] = useState({});

    const zurichTime = getZurichTime(booking.start_before);
    const [selectedTime, setSelectedTime] = useState(zurichTime);

    const [selectedTherapists, setSelectedTherapists] = useState(() => {
        const therapists = booking.therapists ?? [booking.therapist];
        return booking.isDuo ? therapists.slice(0, 2) : [therapists[0]];
    });
    const [duo, setDuo] = useState(booking.isDuo);
    const [message, setMessage] = useState(booking.message || '');
    const [price, setPrice] = useState(booking.isDuo ? (booking.products ?? []).reduce((sum, p) => sum + (p?.price || 0), 0) : booking.product?.price ?? 0);
    const [duration] = useState(booking.duration);
    const [product] = useState(booking.product);
    const [selectedProducts, setSelectedProducts] = useState(() => {
        return Array.isArray(booking.products)
            ? booking.products
            : [booking.product ?? null];
    });

    useEffect(() => {
        setSelectedProducts(
            Array.isArray(booking.products)
                ? booking.products
                : [booking.product ?? null]
        );
    }, [booking.products, booking.product]);

    // Always use translated products for display
    const translatedSelectedProducts = useTranslatableData(selectedProducts);
    const firstProd = translatedSelectedProducts.find(Boolean);

    const originFlow = booking.origin_flow;
    const [flowType, setFlowType] = useState(originFlow);

    const [categoryProductsList, setCategoryProductsList] = useState([[], []]);

    // Also translate category products lists for duration selection (after state declaration)
    const translatedCategoryProductsList = useTranslatableData(categoryProductsList);

    const [collapsedGuests, setCollapsedGuests] = useState(
        (Array.isArray(booking.products) ? booking.products : [booking.product]).map(
            (_p, idx) => idx !== 0
        )
    );
    const [productDropdownOpen, setProductDropdownOpen] = useState(false);

    const toggleGuest = (i) =>
        setCollapsedGuests((prev) => {
            const next = [...prev];
            next[i] = !next[i];
            return next;
        });

    const [showBookingConfirmationModal, setShowBookingConfirmationModal] = useState(false);
    const [therapistIsAvailable, setTherapistIsAvailable] = useState(null);
    const [pendingBookingPayload, setPendingBookingPayload] = useState(null);
    const formattedDate = selectedDate.toLocaleDateString('en-US', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    });

    const formattedTime = selectedTime;

    const [duoInfoOpen, setDuoInfoOpen] = useState(false);
    async function refundThenUpdate(
        bookingId,
        oldTotal,
        newTotal,
        therapists,
        products,
        isDuoFlag
    ) {
        try {
            const updatePayload = {
                therapists,
                products,
                isDuo: isDuoFlag,
                refund_type: 'REFUND',
            };
            const quoteResponse = await BookingService.editQuote(bookingId, updatePayload, api);
            const quote = quoteResponse?.data;
            await BookingService.refundDifference(
                {
                    old_total: quote?.old_net_total ?? oldTotal,
                    new_total: quote?.new_net_total ?? newTotal,
                    locale: i18n.language
                },
                bookingId,
                api
            );

            await BookingService.update(
                bookingId,
                updatePayload,
                api
            );
        } finally {
        }
    }

    const therapist1Info = useMemo(() => ({
        name: selectedTherapists[0]?.firstname,
        products: selectedProducts.filter(Boolean).filter((_, idx) => idx % 2 === 0),
    }), [selectedTherapists, selectedProducts]);

    const therapist2Info = useMemo(() => ({
        name: selectedTherapists[1]?.firstname,
        products: selectedProducts.filter(Boolean).filter((_, idx) => idx % 2 === 1),
    }), [selectedTherapists, selectedProducts]);

    const [removeIdx, setRemoveIdx] = useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const deleteTherapistAndProducts = async (idx) => {
        const nextTherapists = selectedTherapists.filter((_, i) => i !== idx);
        const nextProducts = selectedProducts.filter(Boolean).filter((_, pIdx) => (pIdx % 2) !== idx % 2);
        const isDuoNow = nextTherapists.length > 1;

        const oldTotal = price;
        const newTotal = nextProducts.reduce((s, p) => s + (p?.price || 0), 0);

        setSelectedTherapists(nextTherapists);
        setSelectedProducts(nextProducts);
        setPrice(newTotal);
        setDuo(isDuoNow);
        setCollapsedGuests(c => c.slice(0, nextProducts.length));
        setConfirmDeleteOpen(false);
        setDuoInfoOpen(false);

        try {
            await refundThenUpdate(
                booking.id,
                oldTotal,
                newTotal,
                nextTherapists.map(t => t.id),
                nextProducts.map(p => p.id),
                isDuoNow
            );

            onUpdate({
                ...booking,
                therapists: nextTherapists,
                products: nextProducts,
                price: newTotal
            });
            // Clean up editingSequential state before navigation
            sessionStorage.removeItem("editingSequential");
            navigate("/commande", { replace: true });
            window.location.reload();
        } catch (err) {
            // Clean up editingSequential state on error
            sessionStorage.removeItem("editingSequential");
            setSelectedTherapists(selectedTherapists);
            setSelectedProducts(selectedProducts);
            setPrice(oldTotal);
            setDuo(duo);
        }
    };

    function useDateTimeChanged(initialDate, selectedDate, initialTimeStr, selectedTimeStr) {
        const initialDateMsRef = useRef(
            new Date(
                initialDate.getFullYear(),
                initialDate.getMonth(),
                initialDate.getDate()
            ).getTime()
        );
        const initialTimeRef = useRef(initialTimeStr);

        return useMemo(() => {
            const dateOnlyMs = new Date(
                selectedDate.getFullYear(),
                selectedDate.getMonth(),
                selectedDate.getDate()
            ).getTime();

            return (
                dateOnlyMs !== initialDateMsRef.current ||
                selectedTimeStr !== initialTimeRef.current
            );
        }, [selectedDate, selectedTimeStr]);
    }


    const [productChangeModalOpen, setProductChangeModalOpen] = useState(false);
    const [priceDifference, setPriceDifference] = useState(0);
    const [productChangeDirection, setProductChangeDirection] = useState(null);
    const [isProcessingChange, setIsProcessingChange] = useState(false);
    const [showRefundSuccess, setShowRefundSuccess] = useState(false);
    const [showOfflinePaymentCancelModal, setShowOfflinePaymentCancelModal] = useState(false);
    const [cancelSuccessMessage, setCancelSuccessMessage] = useState("");

    const dateTimeChanged = useDateTimeChanged(
        bookingDateInZurich,
        selectedDate,
        zurichTime.slice(0, 5),
        selectedTime
    );

    useEffect(() => {
        const hydrateAndFetchData = async () => {
            try {
                const [domainsResponse, productsResponse, treeResponse] = await Promise.all([
                    DomainService.index({ with: 'media,zones', sort: 'ordering' }, api),
                    ProductService.index({ is_hidden: false, is_active: 1, category_id: product?.category_id, with: 'media,category' }, api),
                    DomainService.index({ with: 'categories.products,zones', sort: 'ordering' }, api),
                ]);

                setDomains(domainsResponse);
                setCategoryProducts(productsResponse); // Store raw data, not transformed
                setTreeData(treeResponse.map(domain => ({
                    label: domain.title,
                    value: `domain-${domain.id}`,
                    type: 'domain',
                    children: domain.categories?.map(cat => ({
                        label: cat.title,
                        value: `cat-${cat.id}`,
                        type: 'category',
                        children: cat.products.map(prod => ({
                            label: `${prod.name} - ${prod.duration}min - ${prod.price} CHF`,
                            value: `prod-${prod.id}`,
                            type: 'product',
                            checked: selectedProducts[0]?.id === prod.id,
                            data: prod,
                        })),
                    })),
                })));
            } catch (error) {
                console.error("Error during data hydration:", error);
            } finally {
            }
        };

        hydrateAndFetchData();
    }, [product?.category_id]);

    useEffect(() => {
        const hydrateAndFetchDuoProducts = async () => {
            if (!booking?.id) return;

            try {
                const response = await BookingService.index({ id: booking.id, with: 'products' }, api);
                const bookingProducts = response[0]?.products ?? [];

                const hydrated = await Promise.all(
                    bookingProducts.map(async (prod) => {
                        if (!prod?.category?.title) {
                            try {
                                const res = await ProductService.index(
                                    { id: prod.id, with: 'category' },
                                    api
                                );
                                return Array.isArray(res) ? res[0] : res;
                            } catch (err) {
                                console.warn('Hydration error', prod.id, err);
                            }
                        }
                        return prod;
                    })
                );

                console.log({ hydrated });

                // if (hydrated.length > 1 && !duo) {
                //     setDuo(true);
                // }

                const padded = [...hydrated, ...Array(4 - hydrated.length).fill(null)].slice(0, 4);
                setSelectedProducts(padded); // Store raw data, not transformed
                setPrice(padded.reduce((sum, p) => sum + (p?.price || 0), 0));

                const catProductsList = Array(4).fill([]);
                for (let i = 0; i < padded.length; i++) {
                    const prod = padded[i];
                    if (!prod) {
                        catProductsList[i] = [];
                        continue;
                    }
                    const categoryId = prod.category_id ?? prod.category?.id;
                    if (categoryId) {
                        const list = await ProductService.index(
                            { is_hidden: false, is_active: 1, category_id: categoryId, with: 'media,category' },
                            api
                        );
                        catProductsList[i] = list;
                    } else {
                        catProductsList[i] = [prod];
                    }
                }

                setCategoryProductsList(catProductsList); // Store raw data, not transformed

            } catch (error) {
                console.error("Error fetching or hydrating DUO booking products:", error);

            } finally {
            }
        };

        hydrateAndFetchDuoProducts();
    }, [booking?.id]);



    const navigate = useNavigate();

    // const [selectedProduct, setSelectedProduct] = useState(selectedProducts[0]);
    const [selectedTherapist, setSelectedTherapist] = useState(selectedTherapists[0]);

    const [isEditingPrice, setIsEditingPrice] = useState(false);
    const [isEditingMessage, setIsEditingMessage] = useState(false);
    const [duoToggleRequested, setDuoToggleRequested] = useState(false);
    const [duoModalOpen, setDuoModalOpen] = useState(false);
    const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
    const [domains, setDomains] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [showTherapistDisclaimer, setShowTherapistDisclaimer] = useState(false);


    const [availableTherapistSlots, setAvailableTherapistSlots] = useState([]);
    const [currentTherapistIsAvailable, setCurrentTherapistIsAvailable] = useState(false);
    const [otherTherapistsAvailable, setOtherTherapistsAvailable] = useState([]);
    const [availabilityChecked, setAvailabilityChecked] = useState(false);
    const [treeData, setTreeData] = useState([]);
    const [showProductTree, setShowProductTree] = useState(false);
    const [showSoinDisclaimer, setShowSoinDisclaimer] = useState(false);
    const [walleeScript, setWalleeScript] = useState(null);
    const [paymentUrl, setPaymentUrl] = useState(null);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const therapistId = selectedTherapists[0]?.id; // already exists
    const selectedProductForCheck = selectedProducts[0];
    const [showRefundChoiceModal, setShowRefundChoiceModal] = useState(false);

    const resetEditStateFromBooking = useCallback((nextBooking) => {
        if (!nextBooking) return;
        const nextDate = getZurichDate(nextBooking.start_before);
        const nextTime = getZurichTime(nextBooking.start_before);
        const therapists =
            nextBooking.therapists ??
            (nextBooking.therapist ? [nextBooking.therapist] : []);
        const nextTherapists = nextBooking.isDuo
            ? therapists.slice(0, 2)
            : therapists.slice(0, 1);
        const nextProducts = Array.isArray(nextBooking.products)
            ? nextBooking.products
            : [nextBooking.product ?? null];

        setSelectedDate(nextDate);
        setSelectedTime(nextTime);
        setSelectedTherapists(nextTherapists);
        setSelectedTherapist(nextTherapists[0]);
        setSelectedProducts(nextProducts);
        setCollapsedGuests(nextProducts.map((_p, idx) => idx !== 0));
        setDuo(!!nextBooking.isDuo);
        setMessage(nextBooking.message || "");
        const nextPrice = nextBooking.isDuo
            ? nextProducts.reduce((sum, p) => sum + (p?.price || 0), 0)
            : nextBooking.product?.price ?? 0;
        setPrice(nextPrice);
    }, [getZurichDate, getZurichTime]);

    useEffect(() => {
        if (!rawBookingState) return;
        resetEditStateFromBooking(rawBookingState);
    }, [rawBookingState, resetEditStateFromBooking]);

    const refreshBooking = useCallback(
        async ({ notifyUser = false } = {}) => {
            const bookingId = rawBookingToEditState?.id ?? rawBookingState?.id;
            if (!bookingId) return;
            if (refreshInFlightRef.current) return;
            refreshInFlightRef.current = true;

            try {
                const response = await BookingService.index(
                    {
                        with: 'address,therapists.media,therapists.categories,client,products.category,ratings,hotel,transactions',
                        id: `in:${bookingId}`,
                        per_page: 1,
                    },
                    api
                );

                const list = Array.isArray(response)
                    ? response
                    : Array.isArray(response?.data)
                        ? response.data
                        : [];

                const updated = list[0];
                if (!updated) {
                    notify.warn("Booking not available.");
                    onBack?.();
                    return;
                }

                setRawBookingState(updated);
                setRawBookingToEditState(updated);
                if (notifyUser) {
                    notify.info("Booking updated. Edit form refreshed.");
                }
            } catch (error) {
                console.error("Realtime booking refresh failed:", error);
            } finally {
                refreshInFlightRef.current = false;
            }
        },
        [api, notify, onBack, rawBookingState?.id, rawBookingToEditState?.id]
    );

    useEffect(() => {
        refreshBookingRef.current = refreshBooking;
    }, [refreshBooking]);

    useEffect(() => {
        const bookingId = rawBookingToEditState?.id ?? rawBookingState?.id;
        if (!bookingId) return;

        initPusher({ token: auth?.accessToken });
        const channel = `private-booking.${bookingId}`;
        const handler = (data) => {
            const payload =
                typeof data === "string"
                    ? (() => {
                        try {
                            return JSON.parse(data);
                        } catch (_) {
                            return {};
                        }
                    })()
                    : data || {};

            const action = payload?.action?.toString()?.toLowerCase() || "updated";
            if (action === "deleted") {
                notifyRef.current?.warn("Booking deleted.");
                onBackRef.current?.();
                return;
            }

            refreshBookingRef.current?.({ notifyUser: true });
        };
        const unbind = bindEvent(channel, "booking.updated", handler);

        return () => {
            unbind();
        };
    }, [auth?.accessToken, rawBookingState?.id, rawBookingToEditState?.id]);

    useEffect(() => {
        const bookingId = rawBookingToEditState?.id ?? rawBookingState?.id;
        if (!bookingId) return;

        initPusher({ token: auth?.accessToken });
        const pusher = getPusher();
        if (!pusher) return;

        const handleConnected = () => {
            refreshBookingRef.current?.();
        };

        pusher.connection.bind("connected", handleConnected);

        return () => {
            pusher.connection.unbind("connected", handleConnected);
        };
    }, [auth?.accessToken, rawBookingState?.id, rawBookingToEditState?.id]);

    useEffect(() => {
        const bookingId = rawBookingToEditState?.id ?? rawBookingState?.id;
        if (!bookingId) return;

        const handleOnline = () => {
            refreshBookingRef.current?.();
        };

        window.addEventListener("online", handleOnline);

        return () => {
            window.removeEventListener("online", handleOnline);
        };
    }, [rawBookingState?.id, rawBookingToEditState?.id]);


    useEffect(() => {
        const checkAvailability = async () => {
            setAvailabilityChecked(false);
            if (!selectedDate || !selectedTime || !selectedProducts[0]) return;

            const dateStr = selectedDate.toLocaleDateString('sv-SE', { timeZone: 'Europe/Zurich' });

            try {


                const therapistResponse = await TherapistService.store(
                    {
                        booking_id: booking.id,
                        date: dateStr,
                        time: selectedTime,
                        duration: Number(selectedProductForCheck?.duration),
                        lng: booking.address?.longitude ?? bookingToEdit.address?.longitude,
                        lat: booking.address?.latitude ?? bookingToEdit.address?.latitude,
                        zip_code: booking.address?.zip_code ?? bookingToEdit.address?.zip_code,
                        products: [selectedProductForCheck?.id],
                        is_duo: duo,
                        is_hotel: !!booking.hotel,
                        hotel_id: booking.hotel?.id,
                    },
                    api,
                    { with: 'media,ratings,ratings.creator.media' }
                );

                console.log({ booking, bookingToEdit, dateStr, selectedTime, selectedProductForCheck, duo });
                console.log({ therapistResponse });

                const available = Array.isArray(therapistResponse)
                    ? therapistResponse
                    : Object.values(therapistResponse).flat();

                // **FIX: Ensure selected therapists are always considered available for their own booking**
                const selectedTherapistIds = selectedTherapists.filter(Boolean).map(t => t?.id);

                // Create a set of available therapist IDs from the response
                const availableTherapistIds = new Set(available.map(t => t.id));

                // Add missing selected therapists to the available array
                const missingTherapists = selectedTherapists.filter(Boolean).filter(t =>
                    !availableTherapistIds.has(t.id)
                );

                const enhancedAvailable = [...available, ...missingTherapists];

                let isAvailable = false;

                if (duo) {
                    const therapistIds = selectedTherapists.filter(Boolean).map(t => t?.id);
                    // For duo bookings, check if all selected therapists are available
                    // Since we're editing an existing booking, the selected therapists should be available
                    isAvailable = therapistIds.every(id =>
                        enhancedAvailable.some(t => t.id === id) || selectedTherapistIds.includes(id)
                    );
                } else {
                    const therapistId = selectedTherapists[0]?.id;
                    // For single bookings, the selected therapist should always be available for their own booking
                    isAvailable = enhancedAvailable.some(t => t.id === therapistId) || selectedTherapistIds.includes(therapistId);
                }

                // Filter out selected therapists from "other available" list
                const selectedIds = selectedTherapists.map(t => t?.id).filter(Boolean);
                const others = enhancedAvailable.filter(t => !selectedIds.includes(t.id));

                setCurrentTherapistIsAvailable(isAvailable);
                setOtherTherapistsAvailable(others);
                setAvailableTherapistSlots(enhancedAvailable);

            } catch (error) {
                console.error("Error checking therapist availability:", error);
                // **FIX: In case of error, assume the selected therapist is available for their own booking**
                setCurrentTherapistIsAvailable(true);
                setOtherTherapistsAvailable([]);
                setAvailableTherapistSlots(selectedTherapists.filter(Boolean));
            } finally {

                setAvailabilityChecked(true);
            }
        };

        checkAvailability();
    }, [selectedDate, selectedTime, selectedProducts[0]?.id, duo]);




    // const handleTreeChange = (currentNode, selectedNodes) => {
    //     if (currentNode?.type === 'product') {
    //         setSelectedProduct(currentNode.data);
    //         setPrice(currentNode.data.price);
    //     }
    // };

    const formatCHDateTime = (d) =>
        d.toLocaleString('en-US', { timeZone: 'Europe/Zurich', hour12: false }).replace(',', '');


    const handleUpdateBooking = async (e) => {
        e.preventDefault();
        setIsProcessing(true);
        setErrorMessage('');
        setSuccessMessage('');


        const [hh, mm] = selectedTime.split(":");
        const updatedDate = new Date(selectedDate);
        updatedDate.setHours(hh, mm, 0, 0);

        const startBeforeStr = formatCHDateTime(updatedDate).split(" ")[0];
        const dateStr = formatCHDateTime(updatedDate).split(" ")[0];

        const newProductIds = selectedProducts.map(p => p?.id).filter(Boolean);


        const oldTherapistIds = (booking.therapists ?? [booking.therapist]).map(t => t?.id).filter(Boolean);
        const newTherapistIds = selectedTherapists.filter(Boolean).map(t => t.id);
        const therapistsChanged = JSON.stringify(oldTherapistIds.sort()) !== JSON.stringify(newTherapistIds.sort());

        const addressChanged = (
            bookingToEdit?.address?.id !== booking?.address?.id ||
            JSON.stringify(bookingToEdit?.address) !== JSON.stringify(booking?.address)
        );

        const oldTotal = booking.products?.reduce((s, p) => s + (p?.price || 0), 0) || booking.product?.price || 0;
        const newTotal = selectedProducts.filter(Boolean).reduce((sum, p) => sum + (p?.price || 0), 0)

        const payload = {
            products: newProductIds,
            product_id: !duo ? newProductIds[0] : undefined,
            therapists: newTherapistIds,
            start_before: `${startBeforeStr} ${selectedTime}`,
            date: `${dateStr} ${selectedTime}`,
            isDuo: duo,
            message: message,
            duration: Math.round(
                selectedProducts.filter(Boolean).reduce((sum, p) => sum + parseInt(p?.duration || 0, 10), 0)
            ),
            product_id: !duo ? selectedProducts[0]?.id : undefined,
            address_id: booking.address?.id,
            zip_code: booking.address?.zip_code,
            city: booking.address?.city,
            street: booking.address?.street,
            street_number: booking.address?.streetnumber || booking.address?.street_number,
            state: booking.address?.state,
            state_code: booking.address?.state_code,
            country: booking.address?.country,
            country_code: booking.address?.country_code,
            longitude: booking.address?.longitude,
            latitude: booking.address?.latitude,
            locale: i18n.language,
        };

        console.log("Final payload:", payload);

        try {
            if (therapistsChanged || addressChanged) {
                const isHotelBooking = !!booking.hotel;
                const redirect = isHotelBooking ? "/hotels" : booking.origin_flow === "pro" ? "/location-pro" : "/choose-location";
                navigate(redirect, { state: { fromEditBooking: true } });
                return;
            }

            const quoteResponse = await BookingService.editQuote(booking.id, payload, api);
            const quote = quoteResponse?.data;

            if (quote?.action && quote.action !== "UPDATE_ONLY" && !therapistsChanged && !addressChanged) {
                const quotedPayload = { ...payload, _edit_quote: quote };
                setPriceDifference(Math.abs(Number(quote.delta || 0)));
                setProductChangeDirection(quote.action === "PAY_DIFFERENCE" ? "increase" : "decrease");
                setPendingBookingPayload(quotedPayload);
                
                // Check for offline payment when price decreases (refund scenario)
                if (quote.action === "REFUND_OR_CREDIT" && rawBookingState?.offline_payment) {
                    // Offline payment + refund scenario = show no-refund cancellation modal
                    setShowOfflinePaymentCancelModal(true);
                } else {
                    // Regular flow: show payment or refund modal
                    setProductChangeModalOpen(true);
                }
                
                setIsProcessing(false);
                return;
            }

            if (dateTimeChanged && !therapistsChanged && !addressChanged) {
                setPendingBookingPayload(payload);
                setTherapistIsAvailable(currentTherapistIsAvailable);
                setShowBookingConfirmationModal(true);
                setIsProcessing(false);
                return;
            }
            await BookingService.update(booking.id, payload, api);
            onUpdate &&
                onUpdate({
                    ...booking,
                    products: selectedProducts.filter(Boolean),
                    product: !duo ? selectedProducts[0] : booking.product,
                    price: quote?.new_net_total ?? newTotal,
                    locale: i18n.language,
                });
            // Clean up editingSequential state before navigation
            sessionStorage.removeItem("editingSequential");
            window.location.reload();

        } catch (error) {
            console.error("Booking update error:", error);
            // Clean up editingSequential state on error
            sessionStorage.removeItem("editingSequential");
            setErrorMessage("Erreur lors de la mise à jour ou du traitement du paiement.");
        } finally {
            setIsProcessing(false);
        }
    };

    const getMonthName = (date) => date.toLocaleString(i18n.language, { month: 'long' });
    const getDayName = (date) => date.toLocaleString(i18n.language, { weekday: 'short' });

    const getInitialDaysToShow = () => {
        const maxVisibleMonths = 2;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const endExclusive = new Date(today.getFullYear(), today.getMonth() + maxVisibleMonths + 1, 1);
        const days = [];
        let cur = new Date(today);
        while (cur < endExclusive) {
            days.push(new Date(cur.getFullYear(), cur.getMonth(), cur.getDate()));
            cur.setDate(cur.getDate() + 1);
        }
        const windowSize = 9;

        if (booking && booking.start_before) {
            const anchor = new Date(booking.start_before);
            anchor.setHours(0, 0, 0, 0);
            let startIndex = days.findIndex(d => d.getTime() >= anchor.getTime());
            if (startIndex === -1) startIndex = Math.max(0, days.length - windowSize);
            const maxStart = Math.max(0, days.length - windowSize);
            const clampedStart = Math.min(startIndex, maxStart);
            return days.slice(clampedStart, clampedStart + windowSize);
        }

        return days.slice(0, windowSize);
    };

    const [daysToShow, setDaysToShow] = useState(() => getInitialDaysToShow());
    const [showPopup, setShowPopup] = useState(false);

    const updateDaysToShow = (baseDate) => {
        if (!baseDate || isNaN(baseDate.getTime())) {
            console.error('Invalid baseDate in updateDaysToShow');
            return;
        }

        const maxVisibleMonths = 2;
        const windowSize = 9;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startAllowed = new Date(today);
        const endExclusive = new Date(today.getFullYear(), today.getMonth() + maxVisibleMonths + 1, 1);
        const allDays = [];
        let cur = new Date(startAllowed);
        while (cur < endExclusive) {
            allDays.push(new Date(cur.getFullYear(), cur.getMonth(), cur.getDate()));
            cur.setDate(cur.getDate() + 1);
        }

        const lastAllowedDate = allDays[allDays.length - 1];
        const anchor = new Date(baseDate);
        anchor.setHours(0, 0, 0, 0);
        if (anchor < allDays[0]) anchor.setTime(allDays[0].getTime());
        if (anchor > lastAllowedDate) anchor.setTime(lastAllowedDate.getTime());
        let startIndex = allDays.findIndex(d => d.getTime() >= anchor.getTime());
        if (startIndex === -1) startIndex = Math.max(0, allDays.length - windowSize);

        const maxStart = Math.max(0, allDays.length - windowSize);
        const clampedStart = Math.min(startIndex, maxStart);
        const slice = allDays.slice(clampedStart, clampedStart + windowSize);
        setDaysToShow(slice);
    };


    const getNextHourSlots = (date) => {
        const now = new Date();
        const hours = [];
        if (date?.toDateString() === now?.toDateString()) {
            const minutes = now.getMinutes();
            const nextQuarterHour = Math.ceil(minutes / 15) * 15;
            now.setMinutes(nextQuarterHour, 0);
            for (let i = 0; i < 57; i++) {
                const slot = new Date(now.getTime() + i * 15 * 60000);
                if (slot.getHours() >= 8 && slot.getHours() < 22) {
                    hours.push(slot.toLocaleTimeString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                        timeZone: "Europe/Zurich",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                    }));
                }
            }
        } else {
            const startOfDay = new Date(date);
            startOfDay.setHours(8, 0, 0, 0);
            for (let i = 0; i < 57; i++) {
                const slot = new Date(startOfDay.getTime() + i * 15 * 60000);
                if (slot.getHours() >= 8 && slot.getHours() < 22) {
                    hours.push(slot.toLocaleTimeString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                        timeZone: "Europe/Zurich",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                    }));
                }
            }
        }
        return hours;
    };

    const hours = getNextHourSlots(selectedDate);

    const handleMonthChange = (direction) => {
        const now = Date.now();
        if (window.lastMonthChange && (now - window.lastMonthChange) < 300) return;
        window.lastMonthChange = now;

        const currentDate = selectedDate;
        if (!currentDate || isNaN(currentDate.getTime())) {
            console.error('Invalid date in handleMonthChange');
            return;
        }
        const maxVisibleMonths = 2;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const firstAllowedMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastAllowedMonth = new Date(today.getFullYear(), today.getMonth() + maxVisibleMonths, 1);
        const newDate = new Date(currentDate);
        newDate.setDate(1);
        newDate.setMonth(newDate.getMonth() + direction);
        newDate.setHours(0, 0, 0, 0);
        if (newDate < firstAllowedMonth) return;
        if (newDate > lastAllowedMonth) return;
        if (newDate.getFullYear() === today.getFullYear() && newDate.getMonth() === today.getMonth()) {
            newDate.setDate(today.getDate());
        } else {
            newDate.setDate(1);
        }
        const lastAllowedDate = new Date(today.getFullYear(), today.getMonth() + maxVisibleMonths + 1, 0);
        if (newDate > lastAllowedDate) newDate.setTime(lastAllowedDate.getTime());

        setSelectedDate(newDate);
        updateDaysToShow(newDate);
    };
    const handleDayChange = (direction) => {
        const now = Date.now();
        if (window.lastDayChange && (now - window.lastDayChange) < 200) return;
        window.lastDayChange = now;

        const currentDate = selectedDate;
        if (!currentDate || isNaN(currentDate.getTime())) {
            console.error('Invalid date in handleDayChange');
            return;
        }

        const container = document.querySelector('.row-date');
        if (!container) return;

        const dayWidth = container.querySelector('.btn-day')?.offsetWidth || 0;
        const containerWidth = container.clientWidth;
        const visibleItems = Math.floor(containerWidth / dayWidth);
        const daysToScroll = Math.max(1, Math.floor(visibleItems - 0.5));
        const maxVisibleMonths = 2;
        const windowSize = 9;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const endExclusive = new Date(today.getFullYear(), today.getMonth() + maxVisibleMonths + 1, 1);
        const allDays = [];
        let cur = new Date(today);
        while (cur < endExclusive) {
            allDays.push(new Date(cur.getFullYear(), cur.getMonth(), cur.getDate()));
            cur.setDate(cur.getDate() + 1);
        }
        const firstVisible = daysToShow && daysToShow.length ? daysToShow[0] : (selectedDate || today);
        let currentStartIndex = allDays.findIndex(d => d.getTime() === new Date(firstVisible.getFullYear(), firstVisible.getMonth(), firstVisible.getDate()).getTime());
        if (currentStartIndex === -1) currentStartIndex = Math.max(0, allDays.length - windowSize);

        const maxStart = Math.max(0, allDays.length - windowSize);
        let newStart = currentStartIndex + direction * daysToScroll;
        if (newStart < 0) newStart = 0;
        if (newStart > maxStart) newStart = maxStart;

        const newWindow = allDays.slice(newStart, newStart + windowSize);
        setDaysToShow(newWindow);
        const newSelected = newWindow.length ? newWindow[0] : (allDays[allDays.length - 1] || today);
        setSelectedDate(newSelected);
        setSelectedTime(null);
        updateDaysToShow(newSelected);

    };
    const scrollLeft = () => {
        const container = document.querySelector('.row-day');
        container?.scrollBy({ left: -200, behavior: 'smooth' });
    };

    const scrollRight = () => {
        const container = document.querySelector('.row-day');
        container?.scrollBy({ left: 200, behavior: 'smooth' });
    };

    const [categoryProducts, setCategoryProducts] = useState([]);

    const confirmAddressChange = () => {
        // Save current editing state
        sessionStorage.setItem("modificationBookingId", bookingToEdit.id);
        sessionStorage.setItem("editBookingMode", "true");
        sessionStorage.setItem("editAddressMode", "true");
        sessionStorage.setItem("bookingToEdit", JSON.stringify(bookingToEdit));
        sessionStorage.setItem("oldBooking", JSON.stringify(bookingToEdit));

        const therapists = selectedTherapists.filter(Boolean);

        const addressObj = booking?.address || bookingToEdit.address || {};

        const statePayload = {
            address: {
                id: addressObj.id ?? null,
                street: addressObj.street || "",
                zip_code: addressObj.zip_code || "",
                city: addressObj.city || addressObj.state || "",
                state: addressObj.state || "",
                country: addressObj.country || "Switzerland",
                street_number: addressObj.street_number || addressObj.streetnumber || "",
                state_code: addressObj.state_code || "",
                country_code: addressObj.country_code || "CH",
                lat: addressObj.latitude ?? addressObj.lat ?? null,
                lng: addressObj.longitude ?? addressObj.lng ?? null,
                phone: addressObj.phone || "",
            },
            phone: addressObj.phone || "",
            selectedServices: bookingToEdit.services || [],
            duo: bookingToEdit.isDuo,
            selectedTherapists: therapists,
        };

        sessionStorage.setItem("state", JSON.stringify(statePayload));
        const oldBookingWithTime = { ...bookingToEdit, time: selectedTime };
        sessionStorage.setItem("oldBooking", JSON.stringify(oldBookingWithTime));
        const isHotelBooking = !!booking.hotel;
        const redirect = isHotelBooking ? "/hotels" : flowType === "pro" ? "/location-pro" : "/choose-location";
        navigate(redirect, { state: { fromEditBooking: true } });
    };

    useEffect(() => {
        const scrollToSelectedHour = () => {
            const button = document.querySelector(`.btn-hour.selected`);
            if (button) {
                button.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
        };

        const scrollToSelectedDay = () => {
            const button = document.querySelector(`.day.selected`);
            if (button) {
                button.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
        };

        const fetchDomains = async () => {
            try {

                const response = await DomainService.index(
                    { with: 'media,zones', sort: 'ordering' },
                    api,
                    !auth?.accessToken
                );
                setDomains(response);
            } catch (err) {
                console.error("Error fetching domains:", err);
            } finally {

            }
        };
        fetchDomains();

        const fetchProductsForCategory = async () => {
            if (product?.category_id) {
                try {

                    const response = await ProductService.index(
                        {
                            is_hidden: false,
                            is_active: 1,
                            category_id: product.category_id,
                            with: 'media,category'
                        },
                        api,
                        auth?.accessToken ? false : true
                    );

                    if (response?.length) {
                        setCategoryProducts(response);
                    } else {
                        console.warn("No products found for category (subcategory) ID", product.category_id);
                    }
                } catch (error) {
                    console.error("Error fetching products:", error);
                } finally {

                }
            }
        };

        fetchProductsForCategory();
        console.log("Address sent to sessionStorage:", bookingToEdit.address);
        console.log("Selected DUO Products:", selectedProducts);


        const fetchTreeData = async () => {
            try {

                const domains = await DomainService.index(
                    { with: 'categories.products,zones', sort: 'ordering' },
                    api,
                    !auth?.accessToken
                );

                const structuredTree = domains.map(domain => ({
                    label: domain.title,
                    value: `domain-${domain.id}`,
                    type: 'domain',
                    children: domain.categories?.map(cat => ({
                        label: cat.title,
                        value: `cat-${cat.id}`,
                        type: 'category',
                        children: cat.subcategories?.length
                            ? cat.subcategories.map(sub => ({
                                label: sub.title,
                                value: `sub-${sub.id}`,
                                type: 'subcategory',
                                children: sub.products.map(prod => ({
                                    label: `${prod.name} - ${prod.duration}min - ${prod.price} CHF`,
                                    value: `prod-${prod.id}`,
                                    type: 'product',
                                    checked: selectedProducts[0]?.id === prod.id,
                                    data: prod
                                }))
                            }))
                            : cat.products.map(prod => ({
                                label: `${prod.name} - ${prod.duration}min - ${prod.price} CHF`,
                                value: `prod-${prod.id}`,
                                type: 'product',
                                checked: selectedProducts[0]?.id === prod.id,
                                data: prod
                            }))
                    }))
                }));
                console.log("Generated tree data:", structuredTree);
                setTreeData(structuredTree);
            } catch (err) {
                console.error("Error loading tree data:", err);
            } finally {

            }
        };

        fetchTreeData();


        // Delay to wait until DOM is painted
        setTimeout(scrollToSelectedHour, 100);
    }, [selectedTime, selectedDate, product, selectedProducts[0], auth]);

    // Cleanup editingSequential state on component unmount
    useEffect(() => {
        return () => {
            sessionStorage.removeItem("editingSequential");
        };
    }, []);


    const confirmTherapistChange = () => {
        // 1. Enable edit‑mode for this booking
        sessionStorage.setItem("modificationBookingId", String(booking.id));
        sessionStorage.setItem("editBookingMode", "true");

        const dateStr = selectedDate.toISOString().split("T")[0];

        // Determine if this is a true DUO or sequential booking
        const isSequentialBooking = selectedProducts.length > 1 && selectedTherapists.length === 1;
        const isDuoBooking = selectedProducts.length > 1 && selectedTherapists.length === 2;

        // 2. Snapshot old vs new
        const oldBooking = {
            id: booking.id,
            date: dateStr,
            time: selectedTime,
            products: booking.products ?? [booking.product],
            therapists: booking.therapists ?? [booking.therapist],
            address: booking.address,
            isDuo: booking.isDuo,
        };
        const newBooking = {
            ...oldBooking,
            therapists: isSequentialBooking
                ? [selectedTherapists[0]] // Only 1 therapist for sequential
                : selectedTherapists.filter(Boolean).slice(0, 2), // 2 therapists for DUO
            isDuo: isDuoBooking, // Only true if 2 therapists
        };

        sessionStorage.setItem("oldBooking", JSON.stringify(oldBooking));
        sessionStorage.setItem("newBooking", JSON.stringify(newBooking));
        sessionStorage.setItem("bookingToEdit", JSON.stringify(bookingToEdit));

        // 3. Prepare the mini‑state for the next flow
        const fullAddress = {
            id: booking.address?.id ?? null,
            street: booking.address?.street || "",
            street_number:
                booking.address?.streetnumber ||
                booking.address?.street_number ||
                "",
            city: booking.address?.city || booking.address?.state || "",
            zip_code: booking.address?.zip_code || "",
            state: booking.address?.state || "",
            state_code: booking.address?.state_code || "",
            country: booking.address?.country || "Switzerland",
            country_code: booking.address?.country_code || "CH",
            lat: booking.address?.latitude || booking.address?.lat || null,
            lng: booking.address?.longitude || booking.address?.lng || null,
        };

        const selectedServices = selectedProducts.filter(Boolean).map(prod => ({ product: prod }));

        const followUpState = {
            address: fullAddress,
            phone: booking.address?.phone || "",
            selectedServices,
            selectedTherapists: isSequentialBooking
                ? [selectedTherapists[0]] // Only 1 therapist for sequential
                : selectedTherapists.filter(Boolean).slice(0, 2),
            duo: isDuoBooking, // Only true if 2 therapists
            date: dateStr,
            time: selectedTime,
            selectedDate: selectedDate?.toISOString().split("T")[0] ?? null,
            origin_flow: booking.origin_flow,
            hotel_id: booking.hotel?.id || null,
            hotelId: booking.hotel?.id || null,
            totalDuration: selectedProducts.filter(Boolean).reduce((sum, p) => sum + parseInt(p?.duration || 0, 10), 0),
            totalPrice: selectedProducts.filter(Boolean).reduce((sum, p) => sum + (p?.price || 0), 0),
        };
        sessionStorage.setItem("state", JSON.stringify(followUpState));

        // Add a flag to indicate we're editing a sequential booking
        if (isSequentialBooking) {
            sessionStorage.setItem("editingSequential", "true");
        } else {
            sessionStorage.removeItem("editingSequential");
        }

        // 4. Close modal & redirect
        setShowTherapistDisclaimer(false);
        const isHotelBooking = !!booking.hotel;
        const redirectPath = isHotelBooking
            ? "/hotel-choose-therapist"
            : flowType === "pro"
                ? "/fav-flypro"
                : "/choose-therapist";
        navigate(redirectPath, { state: { fromEditBooking: true } });
    };



    const handleDuoToggle = (e) => {
        // Prevent the checkbox from changing state immediately
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        
        if (originallyDuo) {
            setDuoInfoOpen(true);
        } else {
            setDuoModalOpen(true);
            setDuoToggleRequested(true);
        }
    };

    const confirmSoinChange = () => {
        // Prepare the full address
        const address = booking.address || {};
        const fullAddress = {
            id: address.id,
            street: address.street,
            city: address.city ?? address.state,
            zip_code: address.zip_code,
            street_number: address.streetnumber || address.street_number || '',
            state: address.state,
            state_code: address.state_code,
            country: address.country,
            country_code: address.country_code,
            lat: address.latitude,
            lng: address.longitude,
        };

        const selectedServices = selectedProducts.filter(Boolean).map(prod => ({ product: prod }));

        // Create old and new booking objects to store in session storage
        const oldBooking = {
            id: booking.id,
            date: selectedDate.toISOString().split("T")[0],
            time: selectedTime,
            products: booking.products ?? [booking.product],
            therapists: booking.therapists ?? [booking.therapist],
            address: booking.address,
            price: booking.products?.reduce((sum, p) => sum + (p?.price || 0), 0) || booking.product?.price || 0,
            isDuo: booking.isDuo,
        };

        const newBooking = {
            ...oldBooking,
            products: selectedProducts.filter(Boolean),
            time: selectedTime,
            therapists: selectedTherapists,
            price: selectedProducts.filter(Boolean).reduce((sum, p) => sum + (p?.price || 0), 0),
            isDuo: duo,
        };

        // Save the old and new booking details in session storage for comparison later
        sessionStorage.setItem("modificationBookingId", booking.id);
        sessionStorage.setItem("editBookingMode", "true");
        sessionStorage.setItem("oldBooking", JSON.stringify(oldBooking));
        sessionStorage.setItem("newBooking", JSON.stringify(newBooking));
        sessionStorage.setItem("productChanged", "true");


        sessionStorage.setItem("state", JSON.stringify({
            address: fullAddress,
            phone: address.phone ?? "",
            selectedServices,
            duo: duo,
            selectedDate: selectedDate?.toISOString().split("T")[0] ?? null,
            selectedTime,
            message: message ?? '',
            origin_flow: booking.origin_flow,
            hotel_id: booking.hotel?.id || null,
            hotelId: booking.hotel?.id || null,
            selectedTherapists: selectedTherapists.filter(Boolean).slice(0, 2),
            totalDuration: selectedProducts.filter(Boolean).reduce((sum, p) => sum + parseInt(p?.duration || 0, 10), 0),
            totalPrice: selectedProducts.filter(Boolean).reduce((sum, p) => sum + (p?.price || 0), 0),
        }));

        setShowSoinDisclaimer(false);

        // Redirect to the next step based on the flow type
        const isHotelBooking = !!booking.hotel;
        const redirect = isHotelBooking
            ? "/hotel-choose-service"
            : flowType === "pro"
                ? "/flypro-service"
                : "/choose-service";
        navigate(redirect, { state: { fromEditBooking: true } });
    };


    const showWalleeLightbox = (scriptUrl, paymentUrl, onSuccess) => {
        // Si le handler existe déjà, on démarre directement le loader et la lightbox
        if (window.LightboxCheckoutHandler) {
            window.LightboxCheckoutHandler.startPayment(paymentUrl, () => {
                onSuccess();
            });
            return;
        }

        // Sinon, on télécharge le script Wallee en affichant le loader
        const script = document.createElement('script');
        script.src = scriptUrl;
        script.async = true;
        script.onload = () => {
            // Une fois le script chargé, on démarre la lightbox
            window.LightboxCheckoutHandler.startPayment(paymentUrl, () => {
                onSuccess();
            });
        };
        document.body.appendChild(script);
    };

    const fetchCategoryProductsPerPerson = async () => {
        const catProductsList = Array(4).fill([]);
        for (let i = 0; i < selectedProducts.length; i++) {
            const prod = selectedProducts[i];
            const categoryId = prod?.category_id ?? prod?.category?.id;
            if (categoryId) {
                const list = await ProductService.index(
                    { is_hidden: false, is_active: 1, category_id: categoryId, with: 'media,category' },
                    api
                );
                catProductsList[i] = list;
            } else {
                catProductsList[i] = [prod];
            }
        }
        setCategoryProductsList(catProductsList);
    };

    useEffect(() => {
        if (duo) {
            fetchCategoryProductsPerPerson();
        } else {
            const fetchSoloProducts = async () => {
                if (selectedProducts[0]?.category_id) {
                    try {
                        const list = await ProductService.index({
                            is_hidden: false,
                            is_active: 1,
                            category_id: selectedProducts[0].category_id,
                            with: 'media,category',
                        }, api);
                        setCategoryProductsList([list, []]);
                    } catch (err) {
                        console.warn("Erreur lors du chargement des produits SOLO:", err);
                    }
                }
            };
            fetchSoloProducts();
        }
    }, [duo, selectedProducts]);

    function formatDuration(min) {
        if (!min) return "--";
        const h = Math.floor(min / 60);
        const m = min % 60;
        return m
            ? `${h}h ${m} min`
            : `${h}h`;
    }
    const getCatTitle = p => p?.category?.title ?? '';

    const handleProductRefund = async () => {
        setShowRefundChoiceModal(false);
        setProductChangeModalOpen(false);
        try {
            const { _edit_quote, ...updatePayload } = pendingBookingPayload;
            // refund
            await BookingService.refundDifference(
                {
                    old_total: _edit_quote?.old_net_total,
                    new_total: _edit_quote?.new_net_total,
                    locale: i18n.language
                },
                booking.id,
                api
            );
            updatePayload.refund_type = "REFUND";
            // Update booking
            await BookingService.update(
                booking.id,
                updatePayload,
                api
            );
            notify.success(t('refund_in_progress', { amount: priceDifference }));
            // Clean up editingSequential state before navigation
            sessionStorage.removeItem("editingSequential");
            navigate("/commande");
        } catch (err) {

        } finally {
        }
    };

    const generatePromoCode = () =>
        Math.random()
            .toString(36)
            .substring(2, 8)
            .toUpperCase();

    const handleIssueCredit = async () => {
        setShowRefundChoiceModal(false);
        setProductChangeModalOpen(false);
        try {
            const nowIso = new Date().toISOString();
            const validUntil = new Date();
            validUntil.setMonth(validUntil.getMonth() + 3);
            const { data } = await PromotionService.store(
                {
                    code: generatePromoCode(),
                    type: 'FLATT_OFF',
                    amount: priceDifference,
                    name: `${booking.client.firstname} – crédit modification`,
                    date_creation: nowIso,
                    date_validity: validUntil.toISOString(),
                    total_usage: 1,
                    usage_per_customer: 1,
                    is_active: true,
                    all_clients: false,
                    short_description: t('credit_valid_3_months'),
                    long_description: t('credit_issued_for_modification'),
                },
                api
            );
            const code = data.code;

            await promotion_assignement_service.store(
                { promotion_id: data.id, client_id: booking.client_id },
                api
            );
            const { _edit_quote, ...updatePayload } = pendingBookingPayload;
            updatePayload.refund_type = "FLATT_OFF";
            updatePayload.code = data.code;

            await BookingService.update(
                booking.id,
                updatePayload,
                api
            );
            notify.info(t('credit_issued', { amount: priceDifference, code }));
            // Clean up editingSequential state before navigation
            sessionStorage.removeItem("editingSequential");
            navigate("/commande");
        } catch (err) {

        } finally {
        }
    };

    const handleOfflinePaymentCancel = async () => {
        try {
            const response = await BookingService.cancel(
                booking.id,
                api,
                { params: { option: 'FULL_REFUND' } }
            );
            
            console.log("offline payment cancel response:", response.data);
            setCancelSuccessMessage(t('offline_cancel_success_message', 'Votre commande a été annulée avec succès. Aucun remboursement automatique n\'a été effectué.'));
            setShowOfflinePaymentCancelModal(false);
            
            // Clean up and navigate back
            sessionStorage.removeItem("editingSequential");
            navigate("/commande");

        } catch (err) {
            console.error("Error cancelling offline payment booking:", err);
        }
    };

    useEffect(() => {
        const fetchAvailableHours = async () => {
            const productIds = selectedProducts.map(p => p?.id).filter(Boolean);
            const totalDuration = selectedProducts.reduce((sum, p) => sum + Number((p?.duration || 0)), 0);
            if (selectedDate && selectedTherapist.id && productIds.length && booking.address) {
                try {
                    const availabilities = await AvailableSlotsService.store(
                        {
                            date: new Date(selectedDate).toLocaleDateString('en-CA').split('T')[0],
                            duration: totalDuration,
                            lng: booking.address.longitude,
                            lat: booking.address.latitude,
                            zip_code: booking.address.zip_code,
                            products: productIds,
                            therapists: [selectedTherapist.id],
                            is_hotel: !!booking.hotel,
                            hotel_id: booking.hotel?.id,
                            booking_id: booking.id,
                        }, api
                    );
                    setAvailableHours(availabilities);
                } catch (error) {
                    console.error('Error fetching available hours:', error);
                }
            }
        };
        fetchAvailableHours();
    }, [selectedDate, selectedTherapist, selectedTime, selectedProducts]);
    return (
        <>
            <div className='commande-edit'>
                <form className="form listItemWithImageParent" onSubmit={handleUpdateBooking}>
                    {/* Header/Nav Section */}
                    <div
                        className="edit-commande-header"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            position: 'relative',
                            background: 'transparent',
                            marginTop: '-60px',
                        }}
                    >
                        <div style={{ flex: 2, display: 'flex', alignItems: 'center', marginLeft: '-150px' }}>
                            <Button
                                color="dark"
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: 0,
                                    minWidth: 0
                                }}
                                onClick={onBack}
                            >
                                <img src="/assets/images/icon-back.svg" alt="Retour" className="w-4 h-4" />
                                {t('commande_edit:back')}
                            </Button>
                        </div>
                        <div style={{ flex: 1, textAlign: 'center' }}>
                            <h2 className="text-2xl font-semibold" style={{ margin: 0 }}>{t('commande_edit:edit_order')}</h2>
                        </div>
                        <div style={{ flex: 1 }}></div>
                    </div>

                    {/* Therapist */}
                    {duo ? (
                        <div className="therapist-duo-container margin-top-min-30">
                            {selectedTherapists
                                .filter(Boolean)
                                .slice(0, 2)
                                .map((therapist, idx) => (
                                    <div key={therapist.id ?? idx} className="listItemWithImage">
                                        <img
                                            className="imageIcon"
                                            alt=""
                                            src={therapist.media?.[0]?.original_url || user}
                                        />
                                        <div className="text">
                                            <div className="title top-min-15">
                                                <div className="kierra">
                                                    {therapist.firstname || `Personne ${idx + 1}`}
                                                </div>
                                                <div className="rating">
                                                    <div className="stars">
                                                        <div className="starSharp">★</div>
                                                    </div>
                                                    <div className="div">
                                                        {therapist.extra?.rating?.toFixed(1) || '5.0'}/5
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <EditIcon
                                            style={{ fontSize: 18, color: '#333' }}
                                            onClick={() => setShowTherapistDisclaimer(true)}
                                        />
                                    </div>
                                ))}
                        </div>
                    ) : (
                        <div className="listItemWithImage margin-top-min-30">
                            <img
                                className="imageIcon"
                                alt=""
                                src={selectedTherapists[0]?.media?.[0]?.original_url || user}
                            />
                            <div className="text">
                                <div className="title top-min-15">
                                    <div className="kierra">{selectedTherapists[0]?.firstname}</div>
                                    <div className="rating">
                                        <div className="stars">
                                            <div className="starSharp">★</div>
                                        </div>
                                        <div className="div">
                                            {selectedTherapists[0]?.extra?.rating?.toFixed(1) || '5.0'}/5
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <EditIcon
                                style={{ fontSize: 18, color: '#333' }}
                                onClick={() => setShowTherapistDisclaimer(true)}
                            />
                        </div>
                    )}

                    {/* Massage DUO toggle */}
                    {originFlow !== 'pro' && (
                        <div className="duo-toggle-row margin-top-min-30">
                            <span className="duo-toggle-row__label">Massage DUO</span>

                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={duo}
                                    onChange={handleDuoToggle}
                                />
                                <span className="slider round" />
                            </label>
                        </div>
                    )}


                    {/* SOLO */}
                    {!duo && translatedCategoryProductsList[0]?.length > 0 && (
                        <div className="duration-tabs margin-top-min-30">
                            {translatedCategoryProductsList[0].map(prod => (
                                <button
                                    key={prod.id}
                                    type="button"
                                    className={`duration-tab ${selectedProducts[0]?.id === prod.id ? 'duration-tab--active' : ''
                                        }`}
                                    onClick={() => {
                                        setSelectedProducts([prod]);
                                        setPrice(prod.price);
                                    }}
                                >
                                    <span className="duration-tab__minutes">{prod.duration}&nbsp;min</span>
                                    <span className="duration-tab__price">{prod.price}&nbsp;chf</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* DUO – Invité(e) */}
                    {duo && (
                        <div className="guest-list margin-top-min-30">
                            {selectedProducts
                                .map((prod, idx) => ({ prod, idx }))
                                .filter(({ prod }) => prod)
                                .map(({ prod, idx }) => (
                                    <GuestCard
                                        key={idx}
                                        index={idx}
                                        product={prod}
                                        durations={translatedCategoryProductsList[idx] || []}
                                        collapsed={collapsedGuests[idx]}
                                        onToggle={toggleGuest}
                                        onSelectDuration={(i, newProd) => {
                                            const updated = [...selectedProducts];
                                            updated[i] = newProd;
                                            setSelectedProducts(updated);
                                            setPrice(updated.reduce((s, p) => s + (p?.price || 0), 0));
                                        }}
                                        t={t}
                                    />
                                ))}
                        </div>
                    )}

                    {/* DateTimeSelector */}
                    <div className="form-masseur margin-top-min-30" style={{ margin: 0, padding: 0 }}>
                        <DateTimeSelector
                            selectedDate={selectedDate}
                            setSelectedDate={setSelectedDate}
                            selectedTime={selectedTime}
                            setSelectedTime={setSelectedTime}
                            totalDuration={duration}
                            daysToShow={daysToShow}
                            hours={hours}
                            getMonthName={getMonthName}
                            getDayName={getDayName}
                            updateDaysToShow={updateDaysToShow}
                            modalIsOpen2={false}
                            handleMonthChange={handleMonthChange}
                            handleDayChange={handleDayChange}
                            handleDayClick={(date) => setSelectedDate(date)}
                            handleHourClick={(hour) => setSelectedTime(hour)}
                            scrollLeft={scrollLeft}
                            scrollRight={scrollRight}
                            unavailableDates={daysToShow.map((d) => d.toDateString())}
                            availableHours={availableHours}
                            buffertime={{ bufferBefore: 0, bufferAfter: 0 }}
                            totalPrice={price}
                            openModal={() => { }}
                            getFilterText={""}
                            handleResetFilter={() => { }}
                            isEditMode={true}
                        />

                    </div>

                    <div className="margin-top-min-30" style={{ margin: '8px 0 0 0', padding: 0, minHeight: 24 }}>
                        {availabilityChecked && (
                            <>
                                {!currentTherapistIsAvailable ? (
                                    <>
                                        <p>🚫 {t('commande_edit:none')}</p>
                                        {otherTherapistsAvailable.length > 0 ? (
                                            <p>✅ {t('commande_edit:other_therapists_available')}</p>
                                        ) : (
                                            <p>❌ {t('commande_edit:no_therapists_available')}</p>
                                        )}
                                    </>
                                ) : (
                                    <p>✅ {t('commande_edit:therapist_available')}</p>
                                )}
                            </>
                        )}
                    </div>

                    <div className="massage-type-block margin-top-min-30">
                        <p style={{ marginBottom: 6, fontWeight: 500 }}>{t('commande_edit:service_type', 'Soin type')}&nbsp;:</p>
                        <div
                            className="massage-type-field"
                            onClick={() => {
                                if (selectedProducts.filter(Boolean).length > 1) {
                                    setProductDropdownOpen(!productDropdownOpen);
                                }
                            }}
                            role="button"
                        >
                            <span className="massage-type-field__label">
                                {duo
                                    ? firstProd
                                        ? `${getCatTitle(firstProd)} – ${firstProd.name} – `
                                        + `${firstProd.duration}\u202F${t('min')} – ${firstProd.price}\u202F${t('chf')}`
                                        : 'Sélectionnez un massage'
                                    : firstProd
                                        ? `${getCatTitle(firstProd)} – ${firstProd.name} – `
                                        + `${firstProd.duration}\u202F${t('min')}`
                                        : 'Sélectionnez un massage'}
                            </span>
                            <div style={{ gap: '25px', display: 'flex' }}>
                                {selectedProducts.filter(Boolean).length > 1 && (
                                    <KeyboardArrowDownIcon
                                        className="guest-card__chevron"
                                        style={{
                                            transform: productDropdownOpen ? 'rotate(-90deg)' : 'rotate(90deg)',
                                            transition: 'transform 0.2s ease',
                                            fontSize: 25
                                        }}
                                    />
                                )}
                                <EditIcon
                                    style={{ fontSize: 23, color: '#333', cursor: 'pointer' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowSoinDisclaimer(true);
                                    }}
                                />
                            </div>
                        </div>

                        {productDropdownOpen && (
                            <div className="form-field">
                                {translatedSelectedProducts.filter(Boolean).slice(1).map((prod, idx, arr) => (
                                    <React.Fragment key={prod.id}>
                                        <div className="massage-type-field">
                                            <span className="massage-type-field__label">
                                                {getCatTitle(prod)} –{' '}
                                                {prod.name} –{' '}
                                                {prod.duration} {t('min')} – {prod.price} {t('chf')}
                                            </span>
                                        </div>
                                        {idx !== arr.length - 1 && <div className="form-field-child" />}
                                    </React.Fragment>
                                ))}
                            </div>
                        )}

                    </div>

                    {/* Duration / Total row  */}
                    <div className="commande-edit duration-total-row margin-top-min-30">
                        <span className="duration-label">
                            {t('duration')}:&nbsp;{formatDuration(duration)}
                        </span>

                        <span className="total-price">
                            {selectedProducts.filter(Boolean).reduce((s, p) => s + (p.price || 0), 0)} {t('chf')}
                        </span>
                    </div>


                    {/* Address */}
                    <div className="address-block margin-top-min-30">
                        <p className="address-label">{t('address')}</p>
                        <div className="form-field address-field">
                            <div className="rue-de-geneva-container">
                                <p className="rue-de-geneva">
                                    {booking?.address?.street} {booking?.address?.street_number}
                                </p>
                                <p className="rue-de-geneva">
                                    {booking?.address?.zip_code} {booking?.address?.state}
                                </p>

                                <p className="rue-de-geneva">{booking?.phone}</p>

                            </div>
                            <EditIcon
                                style={{ fontSize: 18, color: '#333', cursor: 'pointer' }}
                                onClick={() => setShowDisclaimerModal(true)}
                            />
                        </div>
                    </div>

                    {/* Message au flypro  */}
                    <div className="flypro-message-block margin-top-min-30">
                        <p className="flypro-message-label">{t('message_to_flypro')}</p>
                        <div className="form-field message-field" onClick={() => !isEditingMessage && setIsEditingMessage(true)}>

                            {isEditingMessage ? (
                                <textarea
                                    className="message-textarea"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onBlur={() => setIsEditingMessage(false)}
                                    placeholder={t('example_message')}
                                    rows={3}
                                    autoFocus
                                />
                            ) : (
                                <p className="message-placeholder">
                                    {message || t('example_message')}
                                </p>
                            )}

                            {/* pencil */}
                            <EditIcon
                                style={{ fontSize: 18, color: '#333', cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsEditingMessage(true);
                                }}
                            />
                        </div>
                    </div>

                    {showDisclaimerModal && (
                        <Modal
                            isOpen={showDisclaimerModal}
                            onRequestClose={() => {
                                sessionStorage.removeItem("editingSequential");
                                setShowDisclaimerModal(false);
                            }}
                            style={{
                                content: {
                                    width: "90%",
                                    maxWidth: "420px",
                                    maxHeight: "420px",
                                    margin: "auto",
                                    padding: "30px",
                                    borderRadius: "12px",
                                    textAlign: "center",
                                }
                            }}
                        >
                            <h2>{t('commande_edit:change_address')}</h2>
                            <p>
                                {t('commande_edit:address_change_disclaimer')}<br />
                                <span style={{ textDecoration: 'underline' }}>{t('commande_edit:address_change_disclaimer2')}</span><br /><br />
                                {t('commande_edit:address_change_disclaimer3')}
                            </p>
                            <div className="modal-buttons">
                                <button onClick={() => {
                                    sessionStorage.removeItem("editingSequential");
                                    setShowDisclaimerModal(false);
                                }} className="btn btn-secondary">
                                    {t('commande_edit:cancel')}
                                </button>
                                <button onClick={confirmAddressChange} className="btn btn-primary">
                                    {t('commande_edit:continue')}
                                </button>
                            </div>
                        </Modal>
                    )}

                    {/* Submit Button */}
                    <div className="rectangleDiv">
                        <button
                            type="submit"
                            className="btn bg-[#2D2D2D] text-[#FFD700] text-sm px-8 py-3 rounded-xl shadow-md hover:bg-[#1f1f1f] transition duration-200"
                        >
                            {t('commande_edit:update_order')}
                        </button>
                    </div>
                </form>
            </div>
            <Modal
                isOpen={duoModalOpen}
                onRequestClose={() => setDuoModalOpen(false)}
                style={{
                    content: {
                        width: "90%",
                        maxWidth: "420px",
                        maxHeight: "350px",
                        margin: "auto",
                        padding: "30px",
                        borderRadius: "12px",
                        textAlign: "center",
                    }
                }}
            >
                <h2> {t('commande_edit:sorry')}</h2>
                <p>
                    {t('commande_edit:duo_massage_disclaimer')}
                    <br /><br />
                    {t('commande_edit:duo_massage_disclaimer2')}
                </p>
                <div className="modal-buttons">
                    <button onClick={() => setDuoModalOpen(false)} className="btn btn-secondary">
                        {t('commande_edit:close')}
                    </button>
                    <button
                        className="btn btn-primary"
                        style={{ marginLeft: 8 }}
                        onClick={() => {
                            setDuoModalOpen(false);
                            navigate('/');
                        }}
                    >
                        {t('commande_edit:duo_booking')}
                    </button>
                </div>
            </Modal>

            {showTherapistDisclaimer && (
                <Modal
                    isOpen={showTherapistDisclaimer}
                    onRequestClose={() => {
                        sessionStorage.removeItem("editingSequential");
                        setShowTherapistDisclaimer(false);
                    }}
                    style={{
                        content: {
                            width: "90%",
                            maxWidth: "420px",
                            maxHeight: "450px",
                            margin: "auto",
                            padding: "30px",
                            borderRadius: "12px",
                            textAlign: "center",
                        }
                    }}
                >
                    <h2>{t('commande_edit:change_therapist')}</h2>
                    <p>
                        {t('commande_edit:therapist_change_disclaimer')}
                        <br /><br />
                        <span style={{ textDecoration: 'underline' }}>{t('commande_edit:therapist_change_disclaimer2')}</span><br /><br />
                        {t('commande_edit:therapist_change_disclaimer3')}
                    </p>
                    <div className="modal-buttons">
                        <button onClick={() => {
                            sessionStorage.removeItem("editingSequential");
                            setShowTherapistDisclaimer(false);
                        }} className="btn btn-secondary">
                            {t('commande_edit:cancel')}
                        </button>
                        <button onClick={confirmTherapistChange} className="btn btn-primary">
                            {t('commande_edit:continue')}
                        </button>
                    </div>
                </Modal>
            )}

            {showSoinDisclaimer && (
                <Modal
                    isOpen={showSoinDisclaimer}
                    onRequestClose={() => setShowSoinDisclaimer(false)}
                    style={{
                        content: {
                            width: "90%",
                            maxWidth: "420px",
                            maxHeight: "420px",
                            margin: "auto",
                            padding: "30px",
                            borderRadius: "12px",
                            textAlign: "center",
                        }
                    }}
                >
                    <h2>{t('commande_edit:change_service')}</h2>
                    <p>
                        {t('commande_edit:service_change_disclaimer1')}<br />
                        <span style={{ textDecoration: 'underline' }}>{t('commande_edit:service_change_disclaimer2')}</span><br /><br />
                        {t('commande_edit:service_change_disclaimer3')}
                    </p>
                    <div className="modal-buttons">
                        <button onClick={() => setShowSoinDisclaimer(false)} className="btn btn-secondary">
                            {t('commande_edit:cancel')}
                        </button>
                        <button onClick={confirmSoinChange} className="btn btn-primary">
                            {t('commande_edit:continue')}
                        </button>
                    </div>
                </Modal>
            )}

            {showBookingConfirmationModal && (
                <Modal
                    isOpen={showBookingConfirmationModal}
                    onRequestClose={() => setShowBookingConfirmationModal(false)}
                    style={{
                        content: {
                            width: "90%",
                            maxWidth: "420px",
                            maxHeight: "350px",
                            margin: "auto",
                            padding: "30px",
                            borderRadius: "12px",
                            textAlign: "center",
                        }
                    }}
                >
                    <h2>{t('commande_edit:change_date_time')}</h2>
                    <p>
                        {therapistIsAvailable ? (
                            <>
                                ✅ {t('commande_edit:therapist_available_modal', { date: formattedDate, time: formattedTime })}<br />
                                {t('commande_edit:change_applied')}
                            </>
                        ) : (
                            <>
                                ❌ {t('commande_edit:flypro_not_available_modal', { date: formattedDate, time: formattedTime })}<br />
                                {t('commande_edit:select_another_slot')}
                            </>
                        )}
                    </p>
                    <div className="modal-buttons" style={{ marginTop: 20 }}>
                        <button onClick={() => setShowBookingConfirmationModal(false)} className="btn btn-secondary">
                            {t('commande_edit:cancel')}
                        </button>
                        {therapistIsAvailable && (
                            <button
                                onClick={async () => {
                                    setIsProcessing(true);
                                    try {
                                        await BookingService.update(booking.id, pendingBookingPayload, api);
                                        onUpdate &&
                                            onUpdate({
                                                ...booking,
                                                products: selectedProducts.filter(Boolean),
                                                product: !duo ? selectedProducts[0] : booking.product,
                                                price: pendingBookingPayload.price,
                                                locale: i18n.language,
                                            });
                                        // Clean up editingSequential state before navigation
                                        sessionStorage.removeItem("editingSequential");
                                        navigate("/commande");
                                    } catch (err) {
                                        console.error("Erreur lors de la mise à jour:", err);
                                        setErrorMessage("Erreur lors de la mise à jour de la commande.");
                                    } finally {
                                        setIsProcessing(false);
                                        setShowBookingConfirmationModal(false);
                                    }
                                }}
                                className="btn btn-primary"
                            >
                                {t('confirm')}
                            </button>
                        )}
                    </div>
                </Modal>
            )}

            {productChangeModalOpen && (
                <Modal
                    isOpen={productChangeModalOpen}
                    onRequestClose={() => setProductChangeModalOpen(false)}
                    style={{
                        content: {
                            width: "90%",
                            maxWidth: "480px",
                            maxHeight: "500px",
                            margin: "auto",
                            padding: "50px",
                            borderRadius: "12px",
                            textAlign: "center"
                        }
                    }}
                >
                    {showRefundSuccess ? (
                        <>
                            <h2>{t('service_change_confirmed')}</h2>
                            <p>
                                {t('refund_processed', { amount: Math.round(priceDifference * 0.96) })}
                                {t('confirmation_email_sent')}
                            </p>
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    setProductChangeModalOpen(false);
                                    // Clean up editingSequential state before navigation
                                    sessionStorage.removeItem("editingSequential");
                                    navigate("/commande");
                                }}
                            >
                                {t('close')}
                            </button>
                        </>
                    ) : isProcessingChange ? (
                        <>
                            <h2>{t('please_wait')}</h2>
                            <p>{t('dont_close_page')}</p>
                        </>
                    ) : (
                        <>
                            {productChangeDirection === "increase" ? (
                                <>
                                    <h2>{t('commande_edit:prolongation_products')}</h2><br />
                                    <p>
                                        {t('commande_edit:prolongation_disclaimer1', { priceDifference: priceDifference })}<br /><br />
                                        {t('commande_edit:prolongation_disclaimer2')}<br /><br />
                                        {t('commande_edit:prolongation_disclaimer3')}
                                    </p>
                                    <div className="modal-buttons" style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center' }}>
                                        <button
                                            className="btn btn-primary"
                                            onClick={async () => {
                                                setProductChangeModalOpen(false);
                                                setIsProcessingChange(true);
                                                try {
                                                    const { _edit_quote, ...successUpdate } = pendingBookingPayload;
                                                    const payResponse = await BookingService.payDifference(
                                                        { booking_id: booking.id, new_total: _edit_quote?.new_net_total, duration: pendingBookingPayload.duration, locale: i18n.language,
                                                            success_update: successUpdate,
                                                         },
                                                        api
                                                    );

                                                    const lightboxUrl = payResponse.transaction_data.data.payment_lightbox;
                                                    const paymentUrl = payResponse.transaction_data.data.payment_url;

                                                    if (lightboxUrl && paymentUrl) {
                                                        showWalleeLightbox(lightboxUrl, paymentUrl, () => {
                                                            // Clean up editingSequential state before navigation
                                                            sessionStorage.removeItem("editingSequential");
                                                            navigate("/commande");
                                                        });
                                                    } else {
                                                    }
                                                } catch (err) {
                                                    console.error("Erreur lors du paiement de la différence:", err);
                                                    notify.error(t('error_payment_difference'));

                                                }
                                                finally {
                                                    setIsProcessingChange(false);
                                                }
                                            }}
                                        >
                                            {t('pay_difference')}
                                        </button>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => setProductChangeModalOpen(false)}
                                        >
                                            {t('cancel')}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h2>{t('commande_edit:diminution_products')}</h2><br />
                                    <p>
                                        {t('commande_edit:diminution_disclaimer1', { priceDifference: priceDifference })}<br /><br />
                                        {t('commande_edit:diminution_disclaimer2')}<br /><br />
                                        {t('commande_edit:diminution_disclaimer3')}
                                    </p>
                                    <div className="modal-buttons" style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center' }}>
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleProductRefund}
                                        >
                                            {t('refund')}
                                        </button>
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleIssueCredit}
                                        >
                                            {t('issue_credit')}
                                        </button>
                                    </div>
                                    <div>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => setProductChangeModalOpen(false)}
                                        >
                                            {t('cancel')}
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                </Modal >
            )
            }

            <DuoInfoModal
                open={duoInfoOpen}
                onClose={() => setDuoInfoOpen(false)}
                t1={therapist1Info}
                t2={therapist2Info}
                onAskRemove={(i) => { setRemoveIdx(i); setConfirmDeleteOpen(true); }}
                t={t}
            />
            {/* 
            <Modal
                isOpen={showRefundChoiceModal}
                onRequestClose={() => setShowRefundChoiceModal(false)}
                style={{
                    content: {
                        width: '90%',
                        maxWidth: '400px',
                        margin: 'auto',
                        padding: '30px',
                        borderRadius: '12px',
                        textAlign: 'center',
                    }
                }}
            >
                <h2>Choisissez votre compensation</h2>
                <p>
                    Vous avez diminué la durée du soin de <strong>{priceDifference} CHF</strong>.<br />
                    Souhaitez-vous un remboursement (–4 % frais) ou un avoir du même montant ?
                </p>
                <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        onClick={handleProductRefund}
                        sx={{ flex: 1, textTransform: 'none' }}
                    >
                        Remboursement
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleIssueCredit}
                        sx={{ flex: 1, textTransform: 'none' }}
                    >
                        Émettre un avoir
                    </Button>
                </div>
                <Button
                    onClick={() => setShowRefundChoiceModal(false)}
                    sx={{ marginTop: '16px', textTransform: 'none' }}
                >
                    Annuler
                </Button>
            </Modal> */}


            <Modal
                isOpen={confirmDeleteOpen}
                onRequestClose={() => setConfirmDeleteOpen(false)}
                style={{
                    content: {
                        width: '90%', maxWidth: 420, margin: 'auto',
                        padding: 30, borderRadius: 12, textAlign: 'center'
                    }
                }}
            >
                <h2>{t('delete_therapist')}</h2>
                <p>
                    {t('delete_therapist_message')}
                </p>

                <div style={{ display: 'flex', gap: 14, justifyContent: 'center', marginTop: 30 }}>
                    <button
                        className="btn btn-secondary"
                        onClick={() => setConfirmDeleteOpen(false)}
                    >
                        {t('cancel')}
                    </button>

                    <button
                        className="btn btn-danger"
                        onClick={() => deleteTherapistAndProducts(removeIdx)}
                    >
                        {t('delete')}
                    </button>
                </div>
            </Modal>

            {/* Offline Payment Cancellation Modal */}
            <Modal
                isOpen={showOfflinePaymentCancelModal}
                onRequestClose={() => setShowOfflinePaymentCancelModal(false)}
                style={{
                    content: {
                        width: "90%",
                        maxWidth: "440px",
                        maxHeight: "350px",
                        margin: "auto",
                        padding: "30px",
                        borderRadius: "12px",
                        textAlign: "center"
                    }
                }}
            >
                {!cancelSuccessMessage ? (
                    <>
                        <h2>{t('commande_edit:offline_payment_change_title', 'Modification impossible')}</h2>
                        <p style={{ marginTop: '20px' }}>
                            {t('commande_edit:offline_payment_change_message', 'Cette commande a été payée hors ligne. La modification vers un service moins cher ne peut pas être effectuée automatiquement. Vous pouvez annuler votre commande, mais aucun remboursement automatique ne sera effectué.')}
                        </p>
                        {booking?.offline_payment && hasOnlineTransactions(booking) && (
                            <div style={{ 
                                marginTop: "16px", 
                                padding: "12px", 
                                backgroundColor: "#fff3cd", 
                                borderLeft: "4px solid #ffc107",
                                borderRadius: "4px",
                                fontSize: "13px",
                                textAlign: "left"
                            }}>
                                <p style={{ marginBottom: "6px", color: "#856404", fontWeight: "bold" }}>
                                    ⚠️ {t('partial_refund_title', 'Remboursement Partiel Possible')}
                                </p>
                                <p style={{ marginBottom: "0", color: "#856404", lineHeight: "1.4" }}>
                                    {t('partial_refund_edit_notice', 'Cette commande a des extensions payées en ligne. En cas d\'annulation, seuls les montants payés en ligne pourront être remboursés.')}
                                </p>
                            </div>
                        )}

                        {booking?.offline_payment && !hasOnlineTransactions(booking) && (
                            <div style={{ 
                                marginTop: "16px", 
                                padding: "12px", 
                                backgroundColor: "#f8d7da", 
                                borderLeft: "4px solid #dc3545",
                                borderRadius: "4px",
                                fontSize: "13px",
                                textAlign: "left"
                            }}>
                                <p style={{ marginBottom: "6px", color: "#721c24", fontWeight: "bold" }}>
                                    ❌ {t('no_refund_title', 'Aucun Remboursement')}
                                </p>
                                <p style={{ marginBottom: "0", color: "#721c24", lineHeight: "1.4" }}>
                                    {t('no_refund_edit_offline', 'Cette commande a été entièrement payée hors ligne et ne peut pas être remboursée automatiquement.')}
                                </p>
                            </div>
                        )}
                        <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: "24px" }}>
                            <button
                                className="btn btn-primary"
                                onClick={handleOfflinePaymentCancel}
                                style={{
                                    backgroundColor: "#d32f2f",
                                    color: "white",
                                    padding: "10px 20px",
                                    borderRadius: "8px",
                                    border: "none",
                                    cursor: "pointer"
                                }}
                            >
                                {t('commande_edit:cancel_order', 'Annuler la commande')}
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowOfflinePaymentCancelModal(false)}
                                style={{
                                    backgroundColor: "#393d44",
                                    color: "#faef9b",
                                    padding: "12px 20px",
                                    borderRadius: "8px",
                                    border: "none",
                                    cursor: "pointer"
                                }}
                            >
                                {t('cancel_button', 'Annuler')}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <h2>{t('cancel_message', 'Commande annulée')}</h2>
                        <p style={{ marginTop: '20px' }}>{cancelSuccessMessage}</p>
                        <button
                            onClick={() => {
                                setShowOfflinePaymentCancelModal(false);
                                sessionStorage.removeItem("editingSequential");
                                navigate("/commande");
                            }}
                            style={{
                                marginTop: "24px",
                                padding: "12px 28px",
                                backgroundColor: "#393d44",
                                color: "#faef9b",
                                border: "none",
                                borderRadius: "8px",
                                cursor: "pointer"
                            }}
                        >
                            {t('close_button', 'Fermer')}
                        </button>
                    </>
                )}
            </Modal>
        </>
    );
}

export default CommandeEdit;
